import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import { fetchBankAccounts, addBankAccount, setPrimaryBankAccount } from '../../redux/settingsSlice';
import ToastService from '../../core/notifications/ToastService';

export const BankAccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { bankAccounts, isLoading, error } = useSelector((state: RootState) => state.settings);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    account_holder_name: '',
    account_number: '',
    ifsc_code: '',
    bank_name: '',
    branch_name: '',
    account_type: 'savings'
  });

  useEffect(() => {
    dispatch(fetchBankAccounts());
  }, [dispatch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await dispatch(addBankAccount(formData)).unwrap();
      await dispatch(fetchBankAccounts());
      ToastService.success('Bank account added successfully');
      setOpenDialog(false);
      setFormData({
        account_holder_name: '',
        account_number: '',
        ifsc_code: '',
        bank_name: '',
        branch_name: '',
        account_type: 'savings'
      });
    } catch (err: any) {
      ToastService.error(err || 'Failed to add bank account');
    }
  };

  const handleSetPrimary = async (accountId: string) => {
    try {
      await dispatch(setPrimaryBankAccount(accountId)).unwrap();
      await dispatch(fetchBankAccounts());
      ToastService.success('Primary account updated');
    } catch (err: any) {
      ToastService.error(err || 'Failed to set primary account');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
          <IconButton
            onClick={() => navigate('/settings')}
            sx={{ display: { xs: 'flex', sm: 'none' } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/settings')}
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', md: '2rem' } }}>
              Bank Accounts
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', display: { xs: 'none', sm: 'block' } }}>
              Manage bank accounts for payouts
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{ backgroundColor: '#14B8A6', width: { xs: '100%', sm: 'auto' } }}
        >
          Add Account
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {isLoading && bankAccounts.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : bankAccounts.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" sx={{ color: '#666', mb: 2 }}>
              No bank accounts added yet
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
              Add a bank account to receive payouts
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              sx={{ backgroundColor: '#14B8A6' }}
            >
              Add Your First Account
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Account Holder</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Bank Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Account Number</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>IFSC Code</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bankAccounts.map((account) => (
                <TableRow key={account.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {account.account_holder_name}
                      {account.is_primary && (
                        <Chip label="Primary" size="small" color="primary" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{account.bank_name}</TableCell>
                  <TableCell>****{account.account_number.slice(-4)}</TableCell>
                  <TableCell>{account.ifsc_code}</TableCell>
                  <TableCell>
                    <Chip
                      label={account.is_verified ? 'Verified' : 'Pending'}
                      color={account.is_verified ? 'success' : 'warning'}
                      size="small"
                      icon={account.is_verified ? <CheckCircleIcon /> : undefined}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {!account.is_primary && (
                        <IconButton
                          size="small"
                          onClick={() => handleSetPrimary(account.id)}
                          title="Set as primary"
                        >
                          <StarIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Mobile View */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <Grid container spacing={2}>
          {bankAccounts.map((account) => (
            <Grid item xs={12} key={account.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {account.account_holder_name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        {account.bank_name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {account.is_primary && (
                        <Chip label="Primary" size="small" color="primary" />
                      )}
                      <Chip
                        label={account.is_verified ? 'Verified' : 'Pending'}
                        color={account.is_verified ? 'success' : 'warning'}
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                    Account: ****{account.account_number.slice(-4)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                    IFSC: {account.ifsc_code}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {!account.is_primary && (
                      <Button
                        size="small"
                        startIcon={<StarIcon />}
                        onClick={() => handleSetPrimary(account.id)}
                      >
                        Set Primary
                      </Button>
                    )}
                    <Button size="small" startIcon={<EditIcon />}>
                      Edit
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Add Account Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Bank Account</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Account Holder Name"
                  name="account_holder_name"
                  value={formData.account_holder_name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Account Number"
                  name="account_number"
                  value={formData.account_number}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="IFSC Code"
                  name="ifsc_code"
                  value={formData.ifsc_code}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Account Type"
                  name="account_type"
                  value={formData.account_type}
                  onChange={handleChange}
                  select
                  SelectProps={{ native: true }}
                >
                  <option value="savings">Savings</option>
                  <option value="current">Current</option>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bank Name"
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Branch Name (Optional)"
                  name="branch_name"
                  value={formData.branch_name}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" sx={{ backgroundColor: '#14B8A6' }}>
              Add Account
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default BankAccountsPage;
